// Missing fields panel — plan §4.4.2 + M15 U4/DR5.
// "AI Üretimi Başlat" öncesi eksiklerin listesi. Section bazında grupla; composite'te
// satır "Soru › Parça Adı" olur. Tıklama artık yalnızca bölüm değiştirmez: soruya
// (composite'te parçaya) scroll + 1.5sn vurgu + ilk odaklanabilir elemana focus (DR5 —
// bu davranış bu işle geldi, önceden frontend'de scrollIntoView hiç yoktu).

import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { useFormFillStore } from "../../stores/form-fill-store";
import { selectHeldInvalidCodes } from "../../stores/form-fill-flush";
import { useAuthStore } from "../../stores/auth-store";
import { FORM_SAVE_COPY as C } from "../../lib/form-save-copy";

const HIGHLIGHT_MS = 1500;
// Bölüm değişiminin render'ını bekle — hedef DOM'a bu gecikmeden sonra bakılır.
const NAVIGATE_DELAY_MS = 150;

export function navigateToQuestion(questionCode: string, partKey?: string): void {
  const base = `[data-question-code="${CSS.escape(questionCode)}"]`;
  const selector = partKey ? `${base} [data-part-key="${CSS.escape(partKey)}"]` : base;
  const el =
    document.querySelector<HTMLElement>(selector) ?? document.querySelector<HTMLElement>(base);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.style.transition = "box-shadow 0.3s ease";
  el.style.boxShadow = "0 0 0 3px color-mix(in srgb, var(--color-primary) 35%, transparent)";
  el.style.borderRadius = el.style.borderRadius || "8px";
  window.setTimeout(() => {
    el.style.boxShadow = "";
  }, HIGHLIGHT_MS);
  const focusable = el.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])',
  );
  focusable?.focus({ preventScroll: true });
}

/**
 * Soruya git — bölüm değiştirir, köken filtresini temizler (D1: çip aktifken hedef DOM'da
 * olmayabilir), render sonrası scroll + vurgu + focus. Eksik paneli ve kayıt göstergesi
 * (UX P1 K4 "ilk kaydedilemeyen alana git") aynı yolu kullanır.
 */
export function goToQuestion(sectionId: string, questionCode: string, partKey?: string): void {
  const st = useFormFillStore.getState();
  st.setProvenanceFilter("all");
  st.setActiveSection(sectionId);
  window.setTimeout(() => navigateToQuestion(questionCode, partKey), NAVIGATE_DELAY_MS);
}

/** UX P1 K4 — kaydedilemeyen alanların (yoksa R1 bildirimli alanların) ilkine git. */
export function goToFirstInvalid(): void {
  const st = useFormFillStore.getState();
  const invalid = selectHeldInvalidCodes(st);
  const held = new Set(invalid.length > 0 ? invalid : Object.keys(st.supersededByCode));
  const q = st.snapshot?.snapshot.questions.find((qq) => held.has(qq.code));
  if (!q) return;
  goToQuestion(q.sectionId, q.code, st.errorsByCode[q.code]?.[0]?.partKey);
}

interface Props {
  /** Modal kullanımı: gezinmeden önce paneli kapatmak için (aside'da gerekmez). */
  onNavigate?: (() => void) | undefined;
}

export function MissingPanel({ onNavigate }: Props = {}): JSX.Element | null {
  const missing = useFormFillStore((s) => s.missingFields);
  // UX P1 K4/Ö7 — kaydedilemeyen alanlar (soru sırasıyla) + ilk sunucu mesajı
  const unsaved = useFormFillStore(
    useShallow((s) => {
      const held = new Set(selectHeldInvalidCodes(s));
      if (held.size === 0) return [] as string[];
      return (s.snapshot?.snapshot.questions ?? [])
        .filter((q) => held.has(q.code))
        .map((q) => q.code);
    }),
  );
  // 2026-09-30 (Finar) — müşteri "AI üretimi" ifadesini görmez; hazır-mesajı gönderim dilinde.
  const isClient = useAuthStore((s) => s.user?.userKind === "client");

  const grouped = useMemo(() => {
    const m = new Map<string, typeof missing>();
    for (const item of missing) {
      const key = `${item.sectionId}::${item.sectionCode}::${item.sectionLabel}`;
      const arr = m.get(key) ?? [];
      arr.push(item);
      m.set(key, arr);
    }
    return Array.from(m.entries()).map(([k, items]) => {
      const [sectionId, sectionCode, sectionLabel] = k.split("::");
      return {
        sectionId: sectionId!,
        sectionCode: sectionCode!,
        sectionLabel: sectionLabel!,
        items,
      };
    });
  }, [missing]);

  const unsavedBlock =
    unsaved.length > 0 ? <UnsavedBlock codes={unsaved} onNavigate={onNavigate} /> : null;

  if (missing.length === 0) {
    return (
      <div>
        {unsavedBlock}
        <div
          className="px-4 py-3 rounded-lg"
          style={{
            background: "color-mix(in srgb, var(--color-success) 10%, white)",
            color: "var(--color-success)",
            fontSize: "0.85rem",
          }}
        >
          {isClient
            ? "Hazır — tüm zorunlu alanlar dolu. Formu gönderebilirsiniz."
            : "Hazır — tüm zorunlu alanlar dolu. AI üretimi başlatılabilir."}
        </div>
      </div>
    );
  }

  const go = (sectionId: string, questionCode: string, partKey?: string): void => {
    onNavigate?.();
    goToQuestion(sectionId, questionCode, partKey);
  };

  return (
    <div>
      {unsavedBlock}
      <div
        className="flex items-center justify-between mb-2"
        style={{ color: "var(--color-text-2)", fontSize: "0.85rem" }}
      >
        <span>{missing.length} zorunlu alan boş</span>
      </div>
      {/* #013: max-h-96 kaldırıldı — parent aside zaten overflow-y:auto + full
          height alıyor. Bu sayede uzun eksik listesi sütunu doldurur, çift scroll
          olmaz. Modal'da da aynı komponent kullanılıyor; orada modal kendi içinde
          scroll'lanır. */}
      <div className="flex flex-col gap-3">
        {grouped.map((g) => (
          <div key={g.sectionId}>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.7rem",
                color: "var(--color-text-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 4,
              }}
            >
              {g.sectionCode} · {g.sectionLabel}
            </div>
            <ul className="flex flex-col gap-1">
              {g.items.map((it) => (
                <li key={it.partKey ? `${it.questionCode}::${it.partKey}` : it.questionCode}>
                  <button
                    onClick={() => go(g.sectionId, it.questionCode, it.partKey)}
                    className="w-full text-left px-3 py-2 rounded-lg"
                    style={{
                      background: "var(--color-surface)",
                      border: "1px solid var(--color-border)",
                      fontSize: "0.82rem",
                      color: "var(--color-text)",
                    }}
                  >
                    <span style={{ color: "var(--color-error)" }}>→</span> {it.questionLabel}
                    {it.partLabel && (
                      <span style={{ color: "var(--color-text-2)" }}> › {it.partLabel}</span>
                    )}
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        color: "var(--color-text-3)",
                        fontSize: "0.72rem",
                        marginLeft: 6,
                      }}
                    >
                      {it.questionCode}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function UnsavedBlock({
  codes,
  onNavigate,
}: {
  codes: string[];
  onNavigate?: (() => void) | undefined;
}): JSX.Element {
  const questions = useFormFillStore((s) => s.snapshot?.snapshot.questions);
  const errorsByCode = useFormFillStore((s) => s.errorsByCode);
  return (
    <div
      data-testid="missing-unsaved"
      className="mb-3 px-3 py-2 rounded-lg"
      style={{
        background: "color-mix(in srgb, var(--color-error) 8%, white)",
        border: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
        fontSize: "0.82rem",
      }}
    >
      <div style={{ color: "var(--color-error)", fontWeight: 600 }}>
        {C.panelUnsavedTitle(codes.length)}
      </div>
      <p style={{ color: "var(--color-text-2)", fontSize: "0.78rem", margin: "2px 0 6px" }}>
        {C.panelUnsavedLead}
      </p>
      <ul className="flex flex-col gap-1">
        {codes.map((code) => {
          const q = questions?.find((qq) => qq.code === code);
          const first = errorsByCode[code]?.[0];
          if (!q) return null;
          return (
            <li key={code}>
              <button
                type="button"
                onClick={() => {
                  onNavigate?.();
                  goToQuestion(q.sectionId, code, first?.partKey);
                }}
                className="w-full text-left px-3 py-2 rounded-lg"
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text)",
                }}
              >
                <span style={{ color: "var(--color-error)" }}>→</span> {q.label}
                {first && (
                  <span
                    style={{ display: "block", color: "var(--color-error)", fontSize: "0.75rem" }}
                  >
                    {first.message}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
